import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MapPin, Star, Briefcase, ChevronRight, X, Users, CheckCircle2, Clock3, CircleSlash } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useScreenInit } from '../../useScreenInit.js';
import { usePlatform, initials, Availability } from '../../context/PlatformContext';
const availabilityMeta: Record<Availability, {
  label: string;
  cls: string;
  dot: string;
}> = {
  disponibile: {
    label: 'Disponibile',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500'
  },
  occupato: {
    label: 'Occupato',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500'
  },
  off: {
    label: 'Off',
    cls: 'bg-gray-100 text-gray-500 border-gray-200',
    dot: 'bg-gray-400'
  }
};
export function CmTeamPage() {
  useScreenInit();
  const navigate = useNavigate();
  const {
    cleaners,
    jobs,
    activeLoad,
    cleanerAvgScore,
    addCleaner
  } = usePlatform();
  const [isAddOpen, setIsAddOpen] = useState(false);
  // KPI rollups
  const disponibili = cleaners.filter(c => c.availability === 'disponibile').length;
  const occupati = cleaners.filter(c => c.availability === 'occupato').length;
  const off = cleaners.filter(c => c.availability === 'off').length;
  const daAssegnare = jobs.filter(j => j.status === 'da_assegnare').length;
  const kpis = [{
    label: 'Disponibili',
    value: disponibili,
    icon: <CheckCircle2 size={16} data-id="element-2727" />,
    cls: 'bg-emerald-50 text-emerald-600'
  }, {
    label: 'Occupati',
    value: occupati,
    icon: <Clock3 size={16} data-id="element-2728" />,
    cls: 'bg-amber-50 text-amber-600'
  }, {
    label: 'Off',
    value: off,
    icon: <CircleSlash size={16} data-id="element-2729" />,
    cls: 'bg-gray-100 text-gray-500'
  }, {
    label: 'Da assegnare',
    value: daAssegnare,
    icon: <Briefcase size={16} data-id="element-2730" />,
    cls: 'bg-coral/10 text-coral'
  }];
  const [form, setForm] = useState({
    name: '',
    zones: '',
    availability: 'disponibile' as Availability
  });
  const handleSave = () => {
    if (!form.name.trim()) return;
    addCleaner({
      name: form.name.trim(),
      zones: form.zones.split(',').map(z => z.trim()).filter(Boolean),
      availability: form.availability
    });
    setForm({
      name: '',
      zones: '',
      availability: 'disponibile'
    });
    setIsAddOpen(false);
  };
  return <MobileLayout data-id="element-2731">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-2732">
        <div data-id="element-2733">
          <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-2734">Team</h1>
        </div>
        <Button onClick={() => setIsAddOpen(true)} className="w-full sm:w-auto" data-id="element-2735">
          <Plus size={18} data-id="element-2736" />
          Aggiungi cleaner
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-id="element-2737">
        {kpis.map(k => <Card key={k.label} data-id="element-2738">
            <CardContent className="flex items-center gap-3" data-id="element-2739">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${k.cls}`} data-id="element-2740">
                {k.icon}
              </div>
              <div className="min-w-0" data-id="element-2741">
                <div className="text-xl font-bold text-navy leading-tight" data-id="element-2742">
                  {k.value}
                </div>
                <div className="text-xs text-muted-foreground truncate" data-id="element-2743">
                  {k.label}
                </div>
              </div>
            </CardContent>
          </Card>)}
      </div>

      {cleaners.length > 0 ? <div className="space-y-3" data-id="element-2744">
          {cleaners.map(c => {
        const load = activeLoad(c.id);
        const avg = cleanerAvgScore(c.name);
        const meta = availabilityMeta[c.availability];
        return <Card key={c.id} className="hover:border-coral/40 hover:shadow-sm transition-all cursor-pointer" onClick={() => navigate(`/cm/team/${c.id}`)} data-id="element-2745">
                <CardContent className="flex items-center gap-3" data-id="element-2746">
                  <span className="w-11 h-11 rounded-full bg-coral/10 flex items-center justify-center text-sm font-bold text-coral flex-shrink-0" data-id="element-2747">
                    {initials(c.name)}
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-2748">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-2749">
                      <span className="text-sm font-semibold text-navy truncate" data-id="element-2750">
                        {c.name}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${meta.cls}`} data-id="element-2751">
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} data-id="element-2752" />
                        {meta.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap" data-id="element-2753">
                      <span className="flex items-center gap-1 min-w-0" data-id="element-2754">
                        <MapPin size={12} className="flex-shrink-0" data-id="element-2755" />
                        <span className="truncate" data-id="element-2756">
                          {c.zones.length > 0 ? c.zones.join(', ') : '—'}
                        </span>
                      </span>
                      <span className="flex items-center gap-1" data-id="element-2757">
                        <Briefcase size={12} data-id="element-2758" />
                        {load} attivi
                      </span>
                      {avg != null && <span className="flex items-center gap-1" data-id="element-2759">
                          <Star size={12} className="text-amber-500" data-id="element-2760" />
                          {avg}
                        </span>}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-gray-300 flex-shrink-0" data-id="element-2761" />
                </CardContent>
              </Card>;
      })}
        </div> : <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-2762">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-2763">
            <Users size={20} className="text-muted-foreground" data-id="element-2764" />
          </div>
          <p className="text-sm font-medium text-foreground" data-id="element-2765">
            Nessun cleaner nel team
          </p>
          <p className="text-xs text-muted-foreground mt-1" data-id="element-2766">
            Aggiungi il primo cleaner per iniziare ad assegnare gli incarichi.
          </p>
        </div>}

      {/* Add cleaner modal */}
      {isAddOpen && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setIsAddOpen(false)} data-id="element-2767">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Nuovo cleaner" data-id="element-2768">
            <div className="flex items-center justify-between p-4 border-b border-border" data-id="element-2769">
              <h2 className="text-lg font-bold text-navy" data-id="element-2770">Nuovo cleaner</h2>
              <button onClick={() => setIsAddOpen(false)} aria-label="Chiudi" className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-2771">
                <X size={20} className="text-gray-500" data-id="element-2772" />
              </button>
            </div>
            <div className="p-4 space-y-4" data-id="element-2773">
              <div data-id="element-2774">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-2775">
                  Nome
                </label>
                <input type="text" placeholder="Mario R." value={form.name} onChange={e => setForm(f => ({
              ...f,
              name: e.target.value
            }))} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none min-h-[44px]" data-id="element-2776" />
              </div>
              <div data-id="element-2777">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-2778">
                  Zone coperte (separate da virgola)
                </label>
                <input type="text" placeholder="Navigli, Isola" value={form.zones} onChange={e => setForm(f => ({
              ...f,
              zones: e.target.value
            }))} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none min-h-[44px]" data-id="element-2779" />
              </div>
              <div data-id="element-2780">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-2781">
                  Disponibilità
                </label>
                <select value={form.availability} onChange={e => setForm(f => ({
              ...f,
              availability: e.target.value as Availability
            }))} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-2782">
                  <option value="disponibile" data-id="element-2783">Disponibile</option>
                  <option value="occupato" data-id="element-2784">Occupato</option>
                  <option value="off" data-id="element-2785">Off</option>
                </select>
              </div>
            </div>
            <div className="p-4 border-t border-border bg-gray-50 flex justify-end gap-3" data-id="element-2786">
              <Button variant="subtle" onClick={() => setIsAddOpen(false)} data-id="element-2787">
                Annulla
              </Button>
              <Button onClick={handleSave} data-id="element-2788">Salva cleaner</Button>
            </div>
          </div>
        </div>}
    </MobileLayout>;
}